import { useEffect, useRef, useState } from "react";
import { Package, Truck } from "react-feather";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  asignJobVehicle,
  removeAssignedVehicle,
  vehicleAssignmentByJobId,
} from "../../../../api/jobApi";
import { getBulkDropdownData } from "../../../../api/keyvalue";
import { approveBackhaul } from "../../../../api/requestCreation";
import { statusIdsByContexts } from "../../../../api/statusApi";
import {
  AUTHORITY_TYPE,
  FORMS,
  MODULES,
  STATUS_GROUPS,
} from "../../../../const";
import useAuthority from "../../../../hooks/useAuthority";
import useFirstAndLastAuthorizedTab from "../../../../hooks/useFirstAndLastAuthorizedTab";
import ConfirmDialog from "../../../ConfirmDialog";
import Button from "../../../input/Button";
import Tab from "../../../Tab";
import { vehicleAssignmentValidation } from "../../../validations/vehicleAssignment";
import VehicleAssignmentContainer from "./VehicleAssignmentContainer";
import VehicleAssignmentForm from "./VehicleAssignmentForm";

const VehicleAssignment = ({
  type,
  jobId,
  jobCode,
  requestId,
  requestCode,
  jobCurrentStatus,
  staId,
  close,
  vehicleTypeId,
  onBackhaulRejected,
}) => {
  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Transaction,
    section: FORMS.VEHICLE_ASSIGNMENT,
  });

  const [errors, setErrors] = useState("");
  const [vehicleAssignmentEditMode, setVehicleAssignmentEditMode] =
    useState(false);
  const [containerEditMode, setContainerEditMode] = useState(false);
  const [vehicleAssignmentCurrent, setVehicleAssignmentCurrent] = useState({});
  const [containerCurrent, setContainerCurrent] = useState([]);
  const [reasonToChangeOpt, setReasonToChangeOpt] = useState([]);
  const [trailerTypeOpt, setTrailerTypeOpt] = useState([]);
  const [activeOpt, setActiveOpt] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [yesNo, setYesNo] = useState([]);
  const [vaeList, setVaeList] = useState([]);
  const [vceList, setVceList] = useState([]);
  const [vreList, setVreList] = useState([]);
  const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);
  const [backhaulRejected, setBackhaulRejected] = useState(false);

  const vehicleAssignmentRef = useRef();
  const containerRef = useRef();
  const user = useSelector((state) => state.user);

  const [tab, setTab] = useState(firstViableTab);
  const [isAssignButtonEnabled, setIsAssignButtonEnabled] = useState(false);

  const { authorized } = useAuthority({
    type: AUTHORITY_TYPE.FORM,
    module: MODULES.Transaction,
    form: FORMS.VEHICLE_ASSIGNMENT.VEHICLE.form,
    editMode: vehicleAssignmentEditMode || containerEditMode,
    authenticated: true,
  });

  console.log("Current Status:", jobCurrentStatus);

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const {
        vehicle_change: reasonToChange,
        active: activeData,
        trailor_type: trailerTypeData,
        yesno: yesNoOptions,
      } = await getBulkDropdownData([
        { key: "vehicle_change" },
        { key: "active" },
        { key: "trailor_type" },
        { key: "yesno" },
      ]);

      setReasonToChangeOpt(reasonToChange);
      setActiveOpt(activeData);
      setTrailerTypeOpt(trailerTypeData);
      setYesNo(yesNoOptions);

      const {
        [STATUS_GROUPS.VEHICLE_ASSIGNMENT_ENABLED]: mappedVAEList = [],
        [STATUS_GROUPS.VEHICLE_ASSIGNMENT_CONTAINER_ENABLED]:
          mappedVCEList = [],
        [STATUS_GROUPS.VEHICLE_REMOVE_ENABLED]: mappedVREList = [],
      } = await statusIdsByContexts([
        STATUS_GROUPS.VEHICLE_ASSIGNMENT_ENABLED,
        STATUS_GROUPS.VEHICLE_ASSIGNMENT_CONTAINER_ENABLED,
        STATUS_GROUPS.VEHICLE_REMOVE_ENABLED,
      ]);
      setVaeList(mappedVAEList);
      setVceList(mappedVCEList);
      setVreList(mappedVREList);
      const vehicleAssignmentData = await vehicleAssignmentByJobId(jobId);
      if (vehicleAssignmentData) {
        setVehicleAssignmentCurrent(vehicleAssignmentData.jobVehicle);
        setVehicleAssignmentEditMode(Boolean(vehicleAssignmentData.jobVehicle));
        if (vehicleAssignmentData.jobContainersResponse && vehicleAssignmentData.jobContainersResponse.length > 0) {
          setContainerCurrent(vehicleAssignmentData.jobContainersResponse);
          setContainerEditMode(true);
        } else {
          setContainerEditMode(false);
        }
      } else {
        setVehicleAssignmentEditMode(false);
        setContainerEditMode(false);
      }
      setIsLoading(false);
    })();
  }, [jobId]);

  const assignContainers = async () => {
    setIsSubmitLoading(true);

    try {
      // get the container payload from the container tab
      const containerData = containerRef.current.getData();

      await asignJobVehicle({
        // Only send container-related data.
        // Create vs Update lists are already handled the same way as assignVehicle.
        jobContainersRequests: containerEditMode ? null : containerData,
        jobContainersUpdateRequests: containerEditMode ? containerData : null,

        // Explicitly indicate we are only updating containers, not the vehicle
        containerUpdate: true,
        vehicleUpdate: false,

        // you likely already have jobId on the server via those objects,
        // but including author keeps it consistent with assignVehicle
        author: user.username,
      });

      setIsSubmitLoading(false);
      toast.success("Containers assigned successfully!", {
        autoClose: 5000,
        containerId: "Modal",
      });
      close();
    } catch (e) {
      setIsSubmitLoading(false);
      toast.error("Error while assigning containers!", {
        containerId: "Modal",
        autoClose: 3000,
      });
      console.error(e);
    }
  };

  const assignVehicle = async () => {
    setIsSubmitLoading(true);
    const data = vehicleAssignmentRef.current.getData();
    const vehicleErrors = vehicleAssignmentValidation(
      data?.jobVehicleAssignment,
    );
    if (Object.keys(vehicleErrors).length > 0) {
      setErrors(vehicleErrors);
      toast.error("Error while assigning vehicle!", {
        containerId: "Modal",
        autoClose: 3000,
      });
      setIsSubmitLoading(false);
    } else {
      try {
        const containerData = containerRef.current.getData();
        await asignJobVehicle({
          ...data,
          jobContainersRequests: containerEditMode ? null : containerData,
          jobContainersUpdateRequests: containerEditMode ? containerData : null,
          containerUpdate: containerEditMode,
          vehicleUpdate: vehicleAssignmentEditMode,
          author: user.username,
        });
        setIsSubmitLoading(false);
        toast.success("Vehicle assigned successfully!", {
          autoClose: 5000,
          containerId: "Modal",
        });
        setTimeout(() => {
          close();
        }, 1500);
      } catch (e) {
        setIsSubmitLoading(false);
        toast.error(e?.response?.data ?? "Error while assigning vehicle!", {
          containerId: "Modal",
          autoClose: 3000,
        });
        console.error(e);
      }
    }
  };

  const openRejectConfirmation = () => {
    setConfirmRejectOpen(true);
  };

  const handleRejectBackhaul = async () => {
    try {
      const payload = {
        originalJobId: vehicleAssignmentCurrent.parentRequestId,
        backhaulRequestId: requestId,
        status: 3,
        approvalSource: "VEHICLE_ASSIGNMENT",
      };

      await approveBackhaul(payload);

      setConfirmRejectOpen(false);
      setBackhaulRejected(true);
      setVehicleAssignmentEditMode(false);
      setIsAssignButtonEnabled(false);

      setVehicleAssignmentCurrent((prev) => ({
        ...prev,
        jobIsBackhaul: false,
        jobTransporter: "",
        transporterValue: "",
        jobVehicle: "",
        vehicleValue: "",
        jobDriver: "",
        driverValue: "",
        jobAdditionalDriverMobile: "",
        jobHelper: "",
        helperValue: "",
        jobSecondaryDriver: "",
        secDriverValue: "",
        jobSecondaryHelper: "",
        secHelperValue: "",
        reasonToChange: "",
        remark: "",
        parentJobId: "",
        parentRequestId: "",
      }));

      vehicleAssignmentRef.current?.resetBackhaulFields();
      if (onBackhaulRejected) {
        onBackhaulRejected();
      }

      toast.success("Backhaul request rejected successfully!", {
        autoClose: 5000,
        containerId: "Modal",
      });
    } catch (e) {
      toast.error("Error while rejecting backhaul request!", {
        containerId: "Modal",
        autoClose: 3000,
      });
      console.error(e);
    }
  };

  const handleRemove = async () => {
    const vehicleData = vehicleAssignmentRef.current.getData();
    const containerData = containerRef.current.getSavedContainerIds();
    const vehId = vehicleData.jobVehicleAssignment.jobVehicle;
    const data = {
      jobId: jobId,
      requestId: requestId,
      containerIds: containerData,
      comment: vehicleData.jobVehicleAssignment.remark,
      vehicleId: vehId,
      driverId: vehicleData.jobVehicleAssignment.jobDriver,
      helperId: vehicleData.jobVehicleAssignment.jobHelper,
      vehicleChange: vehicleData.jobVehicleAssignment.reasonToChange,
      block: vehicleData.jobVehicleAssignment.block === 1,
      fromDate: vehicleData.jobVehicleAssignment.vacFromDate,
      toDate: vehicleData.jobVehicleAssignment.vacToDate,
      author: user.username,
    };

    try {
      await removeAssignedVehicle(data);
      toast.success("Vehicle removed successfully!", {
        autoClose: 5000,
        containerId: "Modal",
      });
      close();
    } catch (e) {
      const message = e?.response?.data || "Error while removing vehicle!";

      toast.error(message, {
        containerId: "Modal",
        autoClose: 3000,
      });

      console.error(e);
    }
  };

  return (
    <>
      <div className="ui tabular active menu attached">
        <Tab
          Icon={Truck}
          onClick={() => {
            setTab(1);
          }}
          active={tab === 1}
          errors={!!errors}
          label="Vehicle Assignment"
          authenticated={true}
          form={FORMS.VEHICLE_ASSIGNMENT.VEHICLE.form}
          module={MODULES.Transaction}
          showMandatory={true}
          isMandatory={true}
        />
        <Tab
          Icon={Package}
          onClick={() => {
            setTab(2);
          }}
          active={tab === 2}
          errors={!!errors}
          label="Containers"
          authenticated={true}
          form={FORMS.VEHICLE_ASSIGNMENT.CONTAINER.form}
          module={MODULES.Transaction}
        />
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <VehicleAssignmentForm
          staId={staId}
          ref={vehicleAssignmentRef}
          jobId={jobId}
          vaeList={vaeList}
          vreList={vreList}
          jobCode={jobCode}
          requestId={requestId}
          requestCode={requestCode}
          handleRemove={handleRemove}
          type={type}
          errors={errors}
          setErrors={setErrors}
          dropdown={{ reasonToChangeOpt, yesNo }}
          isLoading={isLoading}
          currentValue={vehicleAssignmentCurrent}
          editMode={vehicleAssignmentEditMode}
          vehicleTypeId={vehicleTypeId}
          setIsAssignButtonEnabled={setIsAssignButtonEnabled}
          isBackhaul={
            vehicleAssignmentCurrent?.jobIsBackhaul === true &&
            !backhaulRejected
          }
          onRejectBackhaul={openRejectConfirmation}
        />
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 2 ? "block" : "none" }}
      >
        <VehicleAssignmentContainer
          staId={staId}
          vceList={vceList}
          jobId={jobId}
          jobCode={jobCode}
          requestId={requestId}
          requestCode={requestCode}
          ref={containerRef}
          dropdown={{ activeOpt, trailerTypeOpt }}
          isLoading={isLoading}
          currentValue={containerCurrent}
          editMode={containerEditMode}
          setIsAssignButtonEnabled={setIsAssignButtonEnabled}
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "flex-end",
        }}
      >
        {vehicleAssignmentEditMode && (
          <Button
            className="ui primary right floated button"
            onClick={assignContainers}
            disabled={
              !vceList.includes(staId) || !isAssignButtonEnabled || !authorized
            }
            label="Container Assign"
            isLoading={isSubmitLoading}
          />
        )}
        <Button
          className="ui primary right floated button"
          onClick={assignVehicle}
          disabled={
            !vaeList.includes(staId) ||
            !isAssignButtonEnabled ||
            !authorized ||
            (tab === 2 && vehicleAssignmentEditMode)
          }
          label="Assign"
          isLoading={isSubmitLoading}
        />
        <Button
          className="ui secondary right floated button"
          label="Cancel"
          onClick={() => {
            close(false);
          }}
        />
      </div>
      <ConfirmDialog
        open={confirmRejectOpen}
        title="Confirm Reject"
        message="Are you sure you want to reject this backhaul vehicle?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={handleRejectBackhaul}
        onCancel={() => setConfirmRejectOpen(false)}
        confirmClassName="ui red button"
        cancelClassName="ui button"
      />
    </>
  );
};

export default VehicleAssignment;
